import React, { useState, useRef, useEffect } from 'react';
import {
  Card, Button, Space, Typography, Tag, Divider, Input, Modal, Select,
} from 'antd';
import {
  AppleFilled, PhoneOutlined, CheckCircleOutlined,
  GoogleCircleFilled, MailOutlined,
} from '@ant-design/icons';
import { AsYouType } from 'libphonenumber-js';
import firebase from 'firebase/app';

import { COUNTRIES, parseE164, getPlaceholder, formatE164ForDisplay, defaultCountry } from '../../utils/phone';
import { writeCredentialEvent } from '../../utils/credentialEvents';
import { trackSmsVerificationRequested, trackSmsVerificationCompleted } from '../../utils/smsTracking';

const { Option } = Select;
const { Text } = Typography;

function normalizeProvider(providerId) {
  if (!providerId) return null;
  const id = `${providerId}`.toLowerCase();
  if (id === 'google.com') return 'google';
  if (id === 'apple.com') return 'apple';
  if (id === 'password') return 'email';
  if (id === 'phone') return 'phone';
  return id;
}

function getCurrentProviders() {
  const user = firebase.auth().currentUser;
  if (!user) return [];
  return (user.providerData || [])
    .map((p) => normalizeProvider(p.providerId))
    .filter(Boolean);
}

function getProviderEmails() {
  const user = firebase.auth().currentUser;
  if (!user) return {};
  const map = {};
  (user.providerData || []).forEach((p) => {
    const name = normalizeProvider(p.providerId);
    if (name && p.email) map[name] = p.email;
  });
  return map;
}

async function syncProvidersToFirestore(client, verifiedPhone = null, verifiedEmail = null) {
  const user = firebase.auth().currentUser;
  if (!user || !client) return;
  const authProviders = Array.from(new Set(getCurrentProviders()));
  const update = {
    authProviders,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
  if (verifiedPhone) update.phoneNumber = verifiedPhone;
  // Adding/changing an email login updates the profile/notification email (linked default).
  if (verifiedEmail) update.email = verifiedEmail;
  const batch = firebase.firestore().batch();
  batch.set(firebase.firestore().collection('clients').doc(client.id), update, { merge: true });
  batch.set(firebase.firestore().collection('users').doc(user.uid), update, { merge: true });
  await batch.commit();
}

/**
 * Shows current login methods for a client and allows adding Apple or Phone.
 * If client.phoneNumber exists and phone is not yet a login method, surfaces
 * a "Verify your phone number" prompt so the existing number can be used to sign in.
 *
 * isAdmin + adminTargetUid: when set, fetches providers via Cloud Function and
 * removes them via Admin SDK (server-side) instead of the client-SDK unlink path.
 */
export default function AddLoginMethodSection({
  client, onUpdated, isAdmin = false, adminTargetUid = null,
}) {
  const mountedRef = useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  // In admin mode, providers come from a Cloud Function (not currentUser).
  // We seed with Firestore authProviders and enrich with emails on mount.
  const [providers, setProviders] = useState(() => (
    isAdmin && client
      ? (client.authProviders || [])
      : getCurrentProviders()
  ));
  const [providerEmails, setProviderEmails] = useState(() => (
    isAdmin ? {} : getProviderEmails()
  ));

  // Admin mode: fetch full providerData (with emails) via Cloud Function on mount
  React.useEffect(() => {
    if (!isAdmin || !adminTargetUid) return;
    firebase.functions().httpsCallable('userService-adminAuthHandler-getClientAuthProviders')(
      { ownerId: adminTargetUid },
    ).then((result) => {
      if (!mountedRef.current) return;
      const { providerData, phoneNumber } = result.data;
      const providerNames = (providerData || [])
        .map((p) => normalizeProvider(p.providerId)).filter(Boolean);
      setProviders(providerNames);
      const emails = {};
      (providerData || []).forEach((p) => {
        const name = normalizeProvider(p.providerId);
        if (name && p.email) emails[name] = p.email;
      });
      // Sync phone from the TARGET's auth record for display — admin never reads currentUser.
      // (Must be set before setProviderEmails, or the phone never lands in state.)
      if (phoneNumber && !emails.phone) emails.phone = phoneNumber;
      setProviderEmails(emails);
    }).catch((err) => {
      console.warn('[AdminSecurity] getClientAuthProviders failed:', err.message);
    });
  }, [isAdmin, adminTargetUid]); // eslint-disable-line

  const hasGoogle = providers.includes('google');
  const hasApple = providers.includes('apple');
  const hasPhone = providers.includes('phone');
  const hasEmail = providers.includes('email');

  // Only treat as verifiable if stored in E.164 format — local formats like 087… cause "Invalid format" from Firebase
  const existingPhone = client && client.phoneNumber && client.phoneNumber.startsWith('+')
    ? client.phoneNumber : null;

  // The currently verified auth phone (source of truth for login)
  const authPhone = firebase.auth().currentUser?.phoneNumber || null;
  // True when the profile phone has been changed but not yet verified to replace the auth phone
  const isPhoneChangePending = hasPhone && !!existingPhone && authPhone !== existingPhone;

  // Phone number shown in the tag. In admin mode it comes from the TARGET's auth record
  // (fetched via the callable into providerEmails.phone) — NEVER the logged-in admin's
  // currentUser, which would leak/attribute the admin's own number to the client.
  const displayPhone = isAdmin
    ? (providerEmails.phone || existingPhone || null)
    : (firebase.auth().currentUser?.phoneNumber || null);

  // Total linked providers — used to gate the Remove buttons (never allow removing the last one)
  const totalProviders = providers.length;

  // ── Remove provider ──
  const [removeError, setRemoveError] = useState(null);

  // ── Google ──
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);

  // ── Apple ──
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleError, setAppleError] = useState(null);

  // ── Email/password (verified by emailed one-time code: idle → code → password) ──
  const [emailInput, setEmailInput] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailStep, setEmailStep] = useState('idle'); // idle | code | password
  const [emailCode, setEmailCode] = useState('');
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);

  // ── Change email login (reuses the email-OTP stepper) ──
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  // ── Phone ──
  const [phoneStep, setPhoneStep] = useState('idle'); // idle | enter | verify
  const [phoneCountry, setPhoneCountry] = useState(() => defaultCountry(client && client.country));
  const [phoneInput, setPhoneInput] = useState(existingPhone || '');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (emailResendCooldown <= 0) return;
    const t = setTimeout(() => setEmailResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [emailResendCooldown]);

  // Reset input when country changes (skip initial mount — don't clear pre-filled existingPhone)
  const isFirstPhoneCountryRender = useRef(true);
  useEffect(() => {
    if (isFirstPhoneCountryRender.current) {
      isFirstPhoneCountryRender.current = false;
      return;
    }
    setPhoneInput('');
  }, [phoneCountry.code]);

  const phoneE164 = parseE164(phoneInput, phoneCountry.code);
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [successMessage, setSuccessMessage] = useState(null);
  const [showChangePhone, setShowChangePhone] = useState(false);

  const afterLink = async (msg, verifiedPhone = null, verifiedEmail = null) => {
    await syncProvidersToFirestore(client, verifiedPhone, verifiedEmail);
    setProviders(getCurrentProviders());
    setProviderEmails(getProviderEmails());
    setSuccessMessage(msg);
    setShowChangePhone(false);
    if (onUpdated) onUpdated();
  };

  // ── Remove a linked provider ───────────────────────────────────────────────
  const handleRemoveProvider = (firebaseProviderId, label) => {
    const isOAuth = firebaseProviderId === 'google.com' || firebaseProviderId === 'apple.com';
    const content = isOAuth
      ? `If you sign in with this ${label} account before re-adding it here, it will create a separate new Strikepay account. You can re-add it on this page at any time.`
      : firebaseProviderId === 'phone'
        ? `This phone number will be freed. Anyone who receives an OTP to this number could use it to create a separate Strikepay account. You can re-add it on this page at any time.`
        : `This email address will be freed. Anyone who knows the password could use it to create a separate Strikepay account. You can re-add it on this page at any time.`;

    Modal.confirm({
      title: `Remove ${label} as a login method?`,
      content,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setRemoveError(null);
        try {
          if (isAdmin) {
            // Admin path: ALWAYS act on the target client via the Admin SDK callable. Never
            // fall through to the self-service (currentUser) path below — that would strip the
            // logged-in ADMIN's own credential while they think they're managing a client.
            if (!adminTargetUid) {
              setRemoveError('Cannot manage this client’s login methods — no linked user found.');
              return;
            }
            // Cloud Function removes provider via Firebase Admin SDK
            await firebase.functions().httpsCallable(
              'userService-adminAuthHandler-removeClientAuthProvider',
            )({ ownerId: adminTargetUid, clientId: client && client.id, providerId: firebaseProviderId });
            // Re-fetch updated providers
            const result = await firebase.functions().httpsCallable(
              'userService-adminAuthHandler-getClientAuthProviders',
            )({ ownerId: adminTargetUid });
            if (!mountedRef.current) return;
            const { providerData } = result.data;
            setProviders((providerData || []).map((p) => normalizeProvider(p.providerId)).filter(Boolean));
            const emails = {};
            (providerData || []).forEach((p) => {
              const name = normalizeProvider(p.providerId);
              if (name && p.email) emails[name] = p.email;
            });
            setProviderEmails(emails);
            setSuccessMessage(`${label} removed.`);
            writeCredentialEvent({
              uid: adminTargetUid,
              type: firebaseProviderId === 'phone' ? 'phone' : 'email',
              action: 'removed', actor: 'admin', meta: { provider: firebaseProviderId },
            });
            if (onUpdated) onUpdated();
          } else {
            // Self-service path: release server-side. The client-SDK unlink() leaves the
            // email/phone RESERVED (unusable by anyone else) and doesn't clean the mirror.
            // The callable frees the identifier (providersToUnlink), cleans the Firestore
            // mirror, enforces the last-method guard, and writes the audit event.
            await firebase.functions().httpsCallable(
              'userService-adminAuthHandler-removeMyLoginMethod',
            )({ providerId: firebaseProviderId, clientId: client && client.id });
            try { await firebase.auth().currentUser.reload(); } catch (_) { /* noop */ }
            if (!mountedRef.current) return;
            const cur = firebase.auth().currentUser;
            const remaining = (cur && cur.providerData) || [];
            setProviders(remaining.map((p) => normalizeProvider(p.providerId)).filter(Boolean));
            const emails = {};
            remaining.forEach((p) => {
              const name = normalizeProvider(p.providerId);
              if (name && p.email) emails[name] = p.email;
            });
            setProviderEmails(emails);
            setSuccessMessage(`${label} removed as a login method.`);
            if (onUpdated) onUpdated();
          }
        } catch (err) {
          if (mountedRef.current) setRemoveError(err.message || `Could not remove ${label}`);
        }
      },
    });
  };

  // Change email/password reuses the same email-OTP flow as add (see handleConfirmEmailPassword,
  // which writes a 'changed' event when an email login already exists). No unlink/relink.

  // ── Add Google ─────────────────────────────────────────────────────────────
  const handleAddGoogle = async () => {
    setGoogleError(null);
    setGoogleLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.addScope('email');
      const user = firebase.auth().currentUser;
      if (user) {
        await user.linkWithPopup(provider);
      } else {
        await firebase.auth().signInWithPopup(provider);
      }
      if (mountedRef.current) {
        await afterLink('Google added as a login method.');
        const uid = firebase.auth().currentUser && firebase.auth().currentUser.uid;
        if (uid) writeCredentialEvent({
          uid, type: 'email', action: 'added',
          value: getProviderEmails().google || null, meta: { provider: 'google' },
        });
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // silent — user dismissed
      } else if (
        err.code === 'auth/credential-already-in-use'
        || err.code === 'auth/account-exists-with-different-credential'
      ) {
        // The selected Google account's email is already registered as a separate Strikepay
        // account. Firebase won't merge two existing accounts automatically.
        setGoogleError(
          'That Google account is already a separate Strikepay account. '
          + 'You can only link a Google account that has never been used to sign in to Strikepay before. '
          + 'Try a different Google account, or sign in with that Google account and manage it separately.',
        );
      } else {
        setGoogleError(err.message || 'Google sign-in failed');
      }
    } finally {
      if (mountedRef.current) setGoogleLoading(false);
    }
  };

  // ── Add Apple ──────────────────────────────────────────────────────────────
  const handleAddApple = async () => {
    setAppleError(null);
    setAppleLoading(true);
    try {
      const provider = new firebase.auth.OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const user = firebase.auth().currentUser;
      if (user) {
        await user.linkWithPopup(provider);
      } else {
        await firebase.auth().signInWithPopup(provider);
      }
      await afterLink('Apple added as a login method.');
      const uid = firebase.auth().currentUser && firebase.auth().currentUser.uid;
      if (uid) writeCredentialEvent({
        uid, type: 'email', action: 'added',
        value: getProviderEmails().apple || null, meta: { provider: 'apple' },
      });
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // user dismissed — silent
      } else if (err.code === 'auth/credential-already-in-use') {
        setAppleError('This Apple account is linked to a different Strikepay account.');
      } else {
        setAppleError(err.message || 'Apple sign-in failed');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  // ── Add Email/password — verified by an emailed one-time code BEFORE the
  // credential is created. enter email → code → password → link (server-side). ──
  const emailCallable = (name) => firebase.functions().httpsCallable(`userService-emailLoginVerifyHandler-${name}`);

  const handleSendEmailCode = async () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedEmail) return;
    setEmailError(null);
    setEmailLoading(true);
    try {
      await emailCallable('requestEmailLoginCode')({ email: trimmedEmail });
      if (!mountedRef.current) return;
      setEmailStep('code');
      setEmailCode('');
      setEmailResendCooldown(30);
    } catch (err) {
      if (mountedRef.current) setEmailError(err.message || 'Could not send the code.');
    } finally {
      if (mountedRef.current) setEmailLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (emailCode.trim().length !== 6) return;
    setEmailError(null);
    setEmailLoading(true);
    try {
      await emailCallable('verifyEmailLoginCode')({ email: trimmedEmail, code: emailCode.trim() });
      if (!mountedRef.current) return;
      setEmailStep('password');
      setEmailPassword('');
    } catch (err) {
      if (mountedRef.current) setEmailError(err.message || 'Invalid or expired code.');
    } finally {
      if (mountedRef.current) setEmailLoading(false);
    }
  };

  const handleConfirmEmailPassword = async () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (emailPassword.length < 6) return;
    setEmailError(null);
    setEmailLoading(true);
    try {
      await emailCallable('confirmEmailLoginPassword')({ email: trimmedEmail, password: emailPassword });
      if (!mountedRef.current) return;
      // Same OTP flow serves both: a change when an email login already exists, else an add.
      const isChange = hasEmail;
      const uid = firebase.auth().currentUser && firebase.auth().currentUser.uid;

      // The server set our primary email + password via the Admin SDK. That invalidates
      // this client's current session (a password change revokes it) and leaves the local
      // providerData stale. Re-sign-in with the credential we just created: same uid, so
      // it keeps the user logged in AND refreshes providerData so the UI flips to "linked".
      try {
        await firebase.auth().signInWithEmailAndPassword(trimmedEmail, emailPassword);
      } catch (reauthErr) {
        console.warn('[AddLoginMethod] re-auth after email link failed:', reauthErr.message);
      }
      if (!mountedRef.current) return;

      if (uid) writeCredentialEvent({ uid, type: 'email', action: isChange ? 'changed' : 'added', value: trimmedEmail });
      await afterLink(
        isChange ? `Email login updated to ${trimmedEmail}.` : `Email login added: ${trimmedEmail}`,
        null,
        trimmedEmail,
      );
      setEmailStep('idle');
      setEmailInput('');
      setEmailCode('');
      setEmailPassword('');
      setShowChangeEmail(false);
    } catch (err) {
      if (mountedRef.current) setEmailError(err.message || 'Could not add email login.');
    } finally {
      if (mountedRef.current) setEmailLoading(false);
    }
  };

  // Shared 3-step email-OTP stepper used by both Add and Change email login.
  // The flow/callables are identical; only the final button label differs.
  const renderEmailOtpStepper = (confirmLabel) => (
    <Space direction="vertical" style={{ width: '100%' }}>
      {emailStep === 'idle' && (
        <>
          <Input
            type="email"
            placeholder="your@email.com"
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value); setEmailError(null); }}
            size="large"
          />
          <Button block icon={<MailOutlined />} type="primary" onClick={handleSendEmailCode} loading={emailLoading} disabled={!emailInput.trim()}>
            Send code
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            We'll email a 6-digit code to verify the address before it can sign you in.
          </Text>
        </>
      )}

      {emailStep === 'code' && (
        <>
          <Text style={{ fontSize: 13 }}>Enter the 6-digit code sent to {emailInput.trim().toLowerCase()}</Text>
          <Input
            placeholder="123456"
            value={emailCode}
            onChange={(e) => { setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setEmailError(null); }}
            size="large"
            maxLength={6}
          />
          <Button block type="primary" onClick={handleVerifyEmailCode} loading={emailLoading} disabled={emailCode.trim().length !== 6}>
            Verify
          </Button>
          <Button type="text" block onClick={handleSendEmailCode} disabled={emailResendCooldown > 0 || emailLoading} style={{ color: emailResendCooldown > 0 ? '#999' : undefined }}>
            {emailResendCooldown > 0 ? `Resend in ${emailResendCooldown}s` : 'Resend code'}
          </Button>
          <Button type="text" block onClick={() => { setEmailStep('idle'); setEmailCode(''); setEmailError(null); }} style={{ color: '#666', fontSize: 12 }}>
            Use a different email
          </Button>
        </>
      )}

      {emailStep === 'password' && (
        <>
          <Text style={{ fontSize: 13 }}>
            <CheckCircleOutlined style={{ color: '#52c41a' }} /> {emailInput.trim().toLowerCase()} verified — set a password to finish.
          </Text>
          <Input.Password
            placeholder="Create a password (6+ characters)"
            value={emailPassword}
            onChange={(e) => { setEmailPassword(e.target.value); setEmailError(null); }}
            size="large"
          />
          <Button block icon={<MailOutlined />} type="primary" onClick={handleConfirmEmailPassword} loading={emailLoading} disabled={emailPassword.length < 6}>
            {confirmLabel}
          </Button>
        </>
      )}

      {emailError && (
        <Text type="danger" style={{ fontSize: 12, display: 'block' }}>{emailError}</Text>
      )}
    </Space>
  );

  // ── Phone: send OTP ────────────────────────────────────────────────────────
  const handleSendCode = async () => {
    if (!phoneE164) return;
    setPhoneError(null);
    setPhoneLoading(true);
    try {
      const container = recaptchaRef.current;
      if (!container) throw new Error('reCAPTCHA container not ready');
      container.innerHTML = '';
      const verifier = new firebase.auth.RecaptchaVerifier(container, { size: 'normal' });
      const result = await firebase.auth().signInWithPhoneNumber(phoneE164, verifier);
      confirmationRef.current = result;
      trackSmsVerificationRequested({
        e164: phoneE164,
        inputCountry: phoneCountry.code,
        profileCountry: client && client.country,
        context: 'add_login',
      });
      setPhoneStep('verify');
      setResendCooldown(60);
    } catch (err) {
      setPhoneError(err.message || 'Failed to send code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleResendCode = () => {
    setPhoneStep('idle');
    setPhoneCode('');
    setPhoneError(null);
    confirmationRef.current = null;
    // phoneInput kept so user can tap "Send verification code" without retyping
  };

  // ── Phone: verify OTP and link/replace ────────────────────────────────────
  const handleVerifyCode = async () => {
    if (phoneCode.length !== 6 || !confirmationRef.current) return;
    setPhoneError(null);
    setPhoneLoading(true);
    const isReplacing = isPhoneChangePending || showChangePhone;
    try {
      const credential = firebase.auth.PhoneAuthProvider.credential(
        confirmationRef.current.verificationId,
        phoneCode.trim(),
      );
      const user = firebase.auth().currentUser;
      if (isReplacing && user) {
        await user.updatePhoneNumber(credential);
      } else if (user) {
        await user.linkWithCredential(credential);
      } else {
        await firebase.auth().signInWithCredential(credential);
      }
      // Sync the profile phone to the AUTHORITATIVE E.164 from the auth record (not the
      // raw national input), so client.phoneNumber always equals the verified login phone.
      // Without this the profile keeps the old number / a malformed value and the
      // "profile differs from login phone — verify" prompt (isPhoneChangePending) loops.
      try { await firebase.auth().currentUser?.reload?.(); } catch (_) { /* noop */ }
      const verifiedE164 = firebase.auth().currentUser?.phoneNumber || phoneE164 || null;
      trackSmsVerificationCompleted({
        e164: verifiedE164,
        inputCountry: phoneCountry.code,
        profileCountry: client && client.country,
        context: 'add_login',
      });
      await afterLink(
        isReplacing
          ? 'Phone number updated and verified as your login method.'
          : 'Phone number verified and added as a login method.',
        verifiedE164,
      );
      const uid = firebase.auth().currentUser && firebase.auth().currentUser.uid;
      if (uid) writeCredentialEvent({
        uid, type: 'phone', action: isReplacing ? 'changed' : 'added', value: verifiedE164,
      });
      setPhoneStep('idle');
      setPhoneCode('');
    } catch (err) {
      if (err.code === 'auth/credential-already-in-use') {
        setPhoneError('This phone number is already linked to a different account.');
      } else {
        setPhoneError(err.message || 'Invalid code. Please try again.');
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <Card title="Login methods" size="small" style={{ marginTop: 16 }}>
      {/* ── Current methods ── */}
      <Space wrap style={{ marginBottom: 4 }}>
        {hasGoogle && (
          <Tag
            color="blue"
            icon={<GoogleCircleFilled />}
            closable={totalProviders > 1}
            onClose={(e) => { e.preventDefault(); handleRemoveProvider('google.com', 'Google'); }}
          >
            Google{providerEmails.google ? ` · ${providerEmails.google}` : ''}
          </Tag>
        )}
        {hasApple && (
          <Tag
            color="default"
            icon={<AppleFilled />}
            closable={totalProviders > 1}
            onClose={(e) => { e.preventDefault(); handleRemoveProvider('apple.com', 'Apple'); }}
          >
            Apple{providerEmails.apple ? ` · ${providerEmails.apple}` : ''}
          </Tag>
        )}
        {hasPhone && (
          <Tag
            color="green"
            icon={<PhoneOutlined />}
            closable={totalProviders > 1}
            onClose={(e) => { e.preventDefault(); handleRemoveProvider('phone', 'Phone'); }}
          >
            Phone{displayPhone ? ` · ${formatE164ForDisplay(displayPhone)}` : ''}
          </Tag>
        )}
        {hasEmail && (
          <Tag
            color="orange"
            icon={<MailOutlined />}
            closable={totalProviders > 1}
            onClose={(e) => { e.preventDefault(); handleRemoveProvider('password', 'Email'); }}
          >
            Email{providerEmails.email ? ` · ${providerEmails.email}` : ''}
          </Tag>
        )}
      </Space>
      {removeError && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{removeError}</Text>
      )}

      {successMessage && (
        <div style={{ marginBottom: 12 }}>
          <Text type="success"><CheckCircleOutlined /> {successMessage}</Text>
        </div>
      )}

      {/* Admin can REMOVE a client's sign-in methods (above), but not ADD them: adding needs
          the account holder's own Google/Apple sign-in or a verification code sent to them. */}
      {isAdmin && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          You can remove this client’s sign-in methods on their behalf. Only the account
          holder can add a new one — it needs their Google/Apple sign-in or a verification
          code sent to them.
        </Text>
      )}

      {!isAdmin && (
      <>
      {/* ── Add Google ── */}
      {!hasGoogle && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>Add Google</Divider>
          <Button
            block
            icon={<GoogleCircleFilled />}
            onClick={handleAddGoogle}
            loading={googleLoading}
            style={{ borderColor: '#4285f4', color: '#4285f4' }}
          >
            Add Google as login method
          </Button>
          {googleError && (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {googleError}
            </Text>
          )}
        </>
      )}

      {/* ── Add Apple ── */}
      {!hasApple && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>Add Apple</Divider>
          <Button
            block
            icon={<AppleFilled />}
            onClick={handleAddApple}
            loading={appleLoading}
            style={{ backgroundColor: '#000', color: '#fff', borderColor: '#444' }}
          >
            Add Apple as login method
          </Button>
          {appleError && (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {appleError}
            </Text>
          )}
        </>
      )}

      {/* ── Add Email/password (verified via emailed code before it becomes a login) ── */}
      {!hasEmail && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>Add email login</Divider>
          {renderEmailOtpStepper('Add email login')}
        </>
      )}

      {/* ── Change email login (same email-OTP flow: verify the new address first) ── */}
      {hasEmail && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>Change email login</Divider>
          {!showChangeEmail ? (
            <Button block onClick={() => { setShowChangeEmail(true); setEmailStep('idle'); setEmailInput(''); setEmailCode(''); setEmailPassword(''); setEmailError(null); }}>
              Change email or password
            </Button>
          ) : (
            <>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Enter the new email — we'll verify it with a code before replacing your old login.
              </Text>
              {renderEmailOtpStepper('Update email login')}
              <Button type="text" block onClick={() => { setShowChangeEmail(false); setEmailStep('idle'); setEmailInput(''); setEmailCode(''); setEmailPassword(''); setEmailError(null); }} style={{ marginTop: 8 }}>
                Cancel
              </Button>
            </>
          )}
        </>
      )}

      {/* ── Change phone (already verified — manual request) ── */}
      {hasPhone && !isPhoneChangePending && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>Change phone number</Divider>
          {!showChangePhone ? (
            <Button block onClick={() => { setShowChangePhone(true); setPhoneInput(''); setPhoneStep('idle'); setPhoneError(null); }}>
              Change phone number
            </Button>
          ) : (
            <>
              {phoneStep === 'idle' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input.Group compact style={{ display: 'flex' }}>
                    <Select
                      value={phoneCountry.code}
                      onChange={(code) => setPhoneCountry(COUNTRIES.find((c) => c.code === code) || COUNTRIES[0])}
                      size="large"
                      style={{ width: 120, flexShrink: 0 }}
                      disabled={phoneLoading}
                      dropdownMatchSelectWidth={false}
                    >
                      {COUNTRIES.map((c) => (
                        <Option key={c.code} value={c.code}>{c.flag} {c.dial}</Option>
                      ))}
                    </Select>
                    <Input
                      placeholder={getPlaceholder(phoneCountry.code)}
                      value={phoneInput}
                      onChange={(e) => {
                        const formatter = new AsYouType(phoneCountry.code);
                        setPhoneInput(formatter.input(e.target.value));
                        setPhoneError(null);
                      }}
                      size="large"
                      style={{ flex: 1 }}
                      disabled={phoneLoading}
                      inputMode="tel"
                      autoFocus
                    />
                  </Input.Group>
                  {phoneE164 && <Text type="secondary" style={{ fontSize: 12 }}>✓ {formatE164ForDisplay(phoneE164)}</Text>}
                  <div ref={recaptchaRef} style={{ display: 'flex', justifyContent: 'center' }} />
                  <Button block icon={<PhoneOutlined />} type="primary" onClick={handleSendCode} loading={phoneLoading} disabled={!phoneE164}>
                    Send verification code
                  </Button>
                  <Button type="text" block onClick={() => { setShowChangePhone(false); setPhoneStep('idle'); setPhoneError(null); }}>
                    Cancel
                  </Button>
                </Space>
              )}
              {phoneStep === 'verify' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text type="secondary">Enter the 6-digit code sent to {formatE164ForDisplay(phoneE164)}</Text>
                  <Input
                    placeholder="123456"
                    value={phoneCode}
                    maxLength={6}
                    onChange={(e) => { setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setPhoneError(null); }}
                    size="large"
                    style={{ textAlign: 'center', letterSpacing: 6, fontSize: 20 }}
                    autoComplete="one-time-code"
                  />
                  <Button block type="primary" onClick={handleVerifyCode} loading={phoneLoading} disabled={phoneCode.length !== 6}>
                    Verify &amp; update
                  </Button>
                  <Button
                    type="text"
                    block
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0}
                    style={{ color: resendCooldown > 0 ? '#555' : '#888' }}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </Button>
                  <Button type="text" block onClick={() => { setPhoneStep('idle'); setPhoneCode(''); setPhoneError(null); setPhoneInput(''); }} style={{ color: '#666', fontSize: 12 }}>
                    Use a different number
                  </Button>
                </Space>
              )}
              {phoneError && <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{phoneError}</Text>}
            </>
          )}
        </>
      )}

      {/* ── Verify / Add / Pending-change Phone ── */}
      {(!hasPhone || isPhoneChangePending) && (
        <>
          <Divider orientation="left" plain style={{ fontSize: 12 }}>
            {isPhoneChangePending ? 'Verify new phone number' : (existingPhone ? 'Verify phone number' : 'Add phone number')}
          </Divider>

          {isPhoneChangePending && phoneStep === 'idle' && (
            <Text type="warning" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Your profile phone ({formatE164ForDisplay(existingPhone)}) differs from your verified login phone ({formatE164ForDisplay(authPhone)}).
              Verify the new number below to use it for sign-in. Your old number will continue to work until then.
            </Text>
          )}

          {phoneStep === 'idle' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.Group compact style={{ display: 'flex' }}>
                <Select
                  value={phoneCountry.code}
                  onChange={(code) => setPhoneCountry(COUNTRIES.find((c) => c.code === code) || COUNTRIES[0])}
                  size="large"
                  style={{ width: 120, flexShrink: 0 }}
                  disabled={phoneLoading}
                  dropdownMatchSelectWidth={false}
                >
                  {COUNTRIES.map((c) => (
                    <Option key={c.code} value={c.code}>{c.flag} {c.dial}</Option>
                  ))}
                </Select>
                <Input
                  placeholder={getPlaceholder(phoneCountry.code)}
                  value={phoneInput}
                  onChange={(e) => {
                    const formatter = new AsYouType(phoneCountry.code);
                    setPhoneInput(formatter.input(e.target.value));
                    setPhoneError(null);
                  }}
                  size="large"
                  style={{ flex: 1 }}
                  disabled={phoneLoading}
                  inputMode="tel"
                />
              </Input.Group>
              {phoneE164 && <Text type="secondary" style={{ fontSize: 12 }}>✓ {formatE164ForDisplay(phoneE164)}</Text>}
              <div ref={recaptchaRef} style={{ display: 'flex', justifyContent: 'center' }} />
              <Button
                block
                icon={<PhoneOutlined />}
                type="primary"
                onClick={handleSendCode}
                loading={phoneLoading}
                disabled={!phoneE164}
              >
                Send verification code
              </Button>
            </Space>
          )}

          {phoneStep === 'verify' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">Enter the 6-digit code sent to {formatE164ForDisplay(phoneE164)}</Text>
              <Input
                placeholder="123456"
                value={phoneCode}
                maxLength={6}
                onChange={(e) => {
                  setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setPhoneError(null);
                }}
                size="large"
                style={{ textAlign: 'center', letterSpacing: 6, fontSize: 20 }}
                autoComplete="one-time-code"
              />
              <Button
                block
                type="primary"
                onClick={handleVerifyCode}
                loading={phoneLoading}
                disabled={phoneCode.length !== 6}
              >
                {isPhoneChangePending ? 'Verify & update' : 'Verify & add'}
              </Button>
              <Button
                type="text"
                block
                onClick={handleResendCode}
                disabled={resendCooldown > 0}
                style={{ color: resendCooldown > 0 ? '#555' : '#888' }}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </Button>
              <Button
                type="text"
                block
                onClick={() => { setPhoneStep('idle'); setPhoneCode(''); setPhoneError(null); setPhoneInput(''); }}
                style={{ color: '#666', fontSize: 12 }}
              >
                Use a different number
              </Button>
            </Space>
          )}

          {phoneError && (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {phoneError}
            </Text>
          )}
        </>
      )}
      </>
      )}
    </Card>
  );
}
